import './LoginForm.css';

import React, { useState, useEffect } from 'react';

import { IoChevronBack } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { loginUser, clearError } from '../../Store/actions/userActions';
import AuthService, { parseAuthError } from '../../services/authService';
import ApiService from '../../services/apiService';
import FirebaseAuthService from '../../services/firebaseAuthService';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import PasswordInput from '../../components/ui/PasswordInput';
import ValidatedInput from '../../components/ui/ValidatedInput';
import ForgotPasswordModal from '../../components/ui/ForgotPasswordModal';
import ToastContainer from '../../components/ui/ToastContainer';
import { sanitizeFormInput, sanitizeEmail } from '../../utils/inputSanitization';
import { API_BASE_URL } from '../../config/runtimeConfig';
import useLaunchAccess from '../../hooks/useLaunchAccess';

const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error: reduxError, isAuthenticated } = useSelector(state => state.user);
  const { loading: launchLoading, canSignUp, selectedMarket } = useLaunchAccess();
  
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });
  const [isMobile, setIsMobile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState({ google: false, apple: false });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [rememberMe, setRememberMe] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/dashboard');

  // Clear any previous errors when component mounts
  useEffect(() => {
    if (reduxError) {
      dispatch(clearError());
    }
  }, [dispatch, reduxError]);

  // Handle redirect logic and remember me on component mount
  useEffect(() => {
    // Get redirect path from URL params or default to dashboard
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirect') || '/dashboard';
    setRedirectPath(redirect);

    // Load remembered email if available
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setLoginData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, []);

  // Handle successful login redirect
  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate(redirectPath);
    }
  }, [isAuthenticated, loading, navigate, redirectPath]);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // OAuth event listeners
  useEffect(() => {
    const handleOAuthSuccess = (event) => {
      const { provider, userData, token, isNewUser } = event.detail;
      setOauthLoading(prev => ({ ...prev, [provider]: false }));
      console.log(`${provider} OAuth success:`, userData);

      // Store auth token
      if (token) {
        localStorage.setItem('token', token);
      }

      // Dispatch Redux login action if available
      if (dispatch && userData) {
        dispatch({ type: 'USER_LOGIN_SUCCESS', payload: { user: userData, token } });
      }

      // Navigate to appropriate page
      if (isNewUser) {
        navigate('/onboard'); // Changed from /onboarding to /onboard
      } else {
        navigate('/dashboard');
      }
    };

    const handleOAuthError = (event) => {
      const { provider, error } = event.detail;
      setOauthLoading(prev => ({ ...prev, [provider]: false }));
      const parsedError = parseAuthError(error);
      if (window.toast) {
        window.toast.error(parsedError.message);
      }
    };

    const handleOAuthCancelled = (event) => {
      const { provider } = event.detail;
      setOauthLoading(prev => ({ ...prev, [provider]: false }));
    };

    window.addEventListener('oauth-success', handleOAuthSuccess);
    window.addEventListener('oauth-error', handleOAuthError);
    window.addEventListener('oauth-cancelled', handleOAuthCancelled);

    return () => {
      window.removeEventListener('oauth-success', handleOAuthSuccess);
      window.removeEventListener('oauth-error', handleOAuthError);
      window.removeEventListener('oauth-cancelled', handleOAuthCancelled);
    };
  }, [navigate]);

  // Validation functions
  const validateEmail = (value) => {
    if (!value) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const validatePassword = (value) => {
    if (!value) return 'Password is required';
    if (value.length < 6) {
      return 'Password must be at least 6 characters';
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Sanitize input based on field type
    let sanitizedValue = value;
    if (name === 'email') {
      sanitizedValue = sanitizeEmail(value);
    } else {
      sanitizedValue = sanitizeFormInput(value);
    }
    
    setLoginData({
      ...loginData,
      [name]: sanitizedValue,
    });

    // Clear field errors when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log("Attempting login with:", loginData);
      await dispatch(loginUser(loginData));
      
      // Handle remember me functionality
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', loginData.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      if (window.toast) {
        window.toast.success("Login successful! Welcome back.");
      }
      // Navigation will be handled by the useEffect that watches isAuthenticated
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      setOauthLoading(prev => ({ ...prev, google: true }));
      const result = await AuthService.initiateGoogleAuth();
      
      // Handle successful Google authentication
      if (result && result.user) {
        // Create user data in the format expected by the backend
        const userData = {
          email: result.user.email,
          name: result.user.displayName,
          photoURL: result.user.photoURL,
          firebaseUid: result.user.uid,
          emailVerified: result.user.emailVerified
        };
        
        // Send to backend for user creation/login
        try {
          const firebaseToken =
            result.firebaseToken ||
            (await FirebaseAuthService.getIdToken());
          if (!firebaseToken) {
            throw new Error('Failed to obtain Firebase ID token');
          }

          const response = await ApiService.fetch('/auth/google', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ firebaseToken })
          });
          const data = await ApiService.handleResponse(response);

          if (data?.token && data?.user) {
            // Store token and update Redux state
            localStorage.setItem('token', data.token);
            dispatch({
              type: 'USER_LOGIN_SUCCESS',
              payload: {
                token: data.token,
                user: data.user
              }
            });
            
            if (window.toast) {
              window.toast.success("Google sign-in successful! Welcome back.");
            }
          } else {
            throw new Error(data.message || 'Google authentication failed');
          }
        } catch (backendError) {
          console.error('Backend authentication error:', backendError);
          throw backendError;
        }
      }
    } catch (error) {
      const parsedError = parseAuthError(error);
      if (window.toast) {
        window.toast.error(parsedError.message);
      }
    } finally {
      setOauthLoading(prev => ({ ...prev, google: false }));
    }
  };

  const handleAppleAuth = async () => {
    console.log('LoginForm.handleAppleAuth called');
    try {
      setOauthLoading(prev => ({ ...prev, apple: true }));
      const result = await AuthService.initiateAppleAuth();
      
      // Handle successful Apple authentication
      if (result && result.user) {
        // Create user data in the format expected by the backend
        const userData = {
          email: result.user.email,
          name: result.user.displayName,
          photoURL: result.user.photoURL,
          appleUid: result.user.uid,
          emailVerified: result.user.emailVerified
        };
        
        // Send to backend for user creation/login
        try {
          const response = await fetch(`${API_BASE_URL}/auth/apple`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userData,
              appleToken: result.token,
              appleCode: result.code
            }),
          });
          
          const data = await response.json();
          
          if (response.ok) {
            // Store token and update Redux state
            localStorage.setItem('token', data.token);
            dispatch({
              type: 'USER_LOGIN_SUCCESS',
              payload: {
                token: data.token,
                user: data.user
              }
            });
            
            if (window.toast) {
              window.toast.success("Apple sign-in successful! Welcome back.");
            }
          } else {
            throw new Error(data.message || 'Apple authentication failed');
          }
        } catch (backendError) {
          console.error('Backend authentication error:', backendError);
          throw backendError;
        }
      }
    } catch (error) {
      setOauthLoading(prev => ({ ...prev, apple: false }));
      const parsedError = parseAuthError(error);
      if (window.toast) {
        window.toast.error(parsedError.message);
      }
    } finally {
      setOauthLoading(prev => ({ ...prev, apple: false }));
    }
  };


  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleForgotPasswordSubmit = async (email) => {
    console.log('Password reset code requested for:', email);
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          .loading-shimmer::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            animation: shimmer 1.5s infinite;
          }
        `}
      </style>
      <div className="hh-auth-page">
      <div className="hh-auth-shell" style={{ flexDirection: isMobile ? 'column' : 'row' }}>
        <button
          type="button"
          className="hh-auth-back"
          onClick={() => navigate('/')}
          aria-label="Back to home"
        >
          <IoChevronBack aria-hidden="true" />
        </button>
        <div className="hh-auth-main" style={{ padding: isMobile ? '40px 24px' : undefined }}>
          <p className="hh-eyebrow" style={{ justifyContent: 'center' }}>Welcome back</p>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '28px' : '36px' }}>
            <h2 className="hh-auth-title">Sign In</h2>
            <p className="hh-auth-lead" style={{ marginBottom: 0 }}>
              Continue your journey with Hebrew Hearts
            </p>
          </div>

          {/* Social Buttons */}
          <div style={{
            display: 'flex',
            gap: '15px',
            marginBottom: '30px',
            justifyContent: 'center'
          }}>
            <button 
              onClick={() => {
                console.log('Apple button clicked!');
                handleAppleAuth();
              }}
              disabled={oauthLoading.apple || isSubmitting}
              style={{
                width: '55px',
                height: '55px',
                borderRadius: '50%',
                border: '2px solid #e5e7eb',
                background: 'white',
                cursor: oauthLoading.apple || isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '1rem' : '1.2rem',
                fontWeight: 'bold',
                color: '#000000',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: oauthLoading.apple || isSubmitting ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!oauthLoading.apple && !isSubmitting) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              {oauthLoading.apple ? (
                <LoadingSpinner size="small" color="primary" />
              ) : (
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="1.2em" 
                  height="1.2em" 
                  fill="currentColor" 
                  viewBox="0 0 16 16"
                  style={{ fontSize: 'inherit' }}
                >
                  <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516s1.52.087 2.475-1.258.762-2.391.728-2.43m3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422s1.675-2.789 1.698-2.854-.597-.79-1.254-1.157a3.7 3.7 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56s.625 1.924 1.273 2.796c.576.984 1.34 1.667 1.659 1.899s1.219.386 1.843.067c.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758q.52-1.185.473-1.282"/>
                  <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516s1.52.087 2.475-1.258.762-2.391.728-2.43m3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422s1.675-2.789 1.698-2.854-.597-.79-1.254-1.157a3.7 3.7 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56s.625 1.924 1.273 2.796c.576.984 1.34 1.667 1.659 1.899s1.219.386 1.843.067c.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758q.52-1.185.473-1.282"/>
                </svg>
              )}
            </button>
            <button 
              onClick={handleGoogleAuth}
              disabled={oauthLoading.google || isSubmitting}
              style={{
                width: '55px',
                height: '55px',
                borderRadius: '50%',
                border: '2px solid #e5e7eb',
                background: 'white',
                cursor: oauthLoading.google || isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '1rem' : '1.2rem',
                fontWeight: 'bold',
                color: '#ea4335',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: oauthLoading.google || isSubmitting ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!oauthLoading.google && !isSubmitting) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 20px rgba(234, 67, 53, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              {oauthLoading.google ? (
                <LoadingSpinner size="small" color="primary" />
              ) : (
                'G'
              )}
            </button>
          </div>

          <p style={{
            textAlign: 'center',
            color: 'var(--hh-fg3)',
            marginBottom: '30px',
            fontSize: isMobile ? '0.9rem' : '0.95rem'
          }}>
            or use your email for login
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <ValidatedInput
              type="email"
              name="email"
              placeholder="Email Address"
              value={loginData.email}
              onChange={handleChange}
              validation={validateEmail}
              style={{
                marginBottom: '20px',
                fontSize: isMobile ? '16px' : '1rem'
              }}
            />
            <PasswordInput
              name="password"
              placeholder="Password"
              value={loginData.password}
              onChange={handleChange}
              style={{
                fontSize: isMobile ? '16px' : '1rem'
              }}
            />
            {/* Forgot Password Link */}
            <div style={{
              textAlign: 'right',
              marginBottom: '20px'
            }}>
              <button
                type="button"
                className="login-link-btn"
                onClick={handleForgotPassword}
              >
                Forgot Password?
              </button>
            </div>
            
            {/* Remember Me Checkbox */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px',
              gap: '8px'
            }}>
              <input
                type="checkbox"
                id="remember-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: 'var(--hh-teal, #5BB5C7)',
                  cursor: 'pointer'
                }}
              />
              <label
                htmlFor="remember-me"
                style={{
                  color: 'var(--hh-fg3)',
                  fontSize: isMobile ? '0.9rem' : '0.95rem',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Remember me
              </label>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || loading}
              className={`login-submit-btn ${isSubmitting || loading ? 'loading-shimmer' : ''}`}
            >
              {isSubmitting || loading ? (
                <>
                  <LoadingSpinner size="small" color="white" />
                  <span style={{ 
                    animation: isSubmitting || loading ? 'pulse 1.5s ease-in-out infinite' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    Signing In...
                  </span>
                </>
              ) : (
                'SIGN IN'
              )}
            </button>
          </form>
        </div>

        <div className="hh-auth-aside" style={{ order: isMobile ? -1 : 0, padding: isMobile ? '32px 24px' : undefined }}>
          {canSignUp ? (
            <>
              <p className="hh-eyebrow" style={{ justifyContent: 'center' }}>New here?</p>
              <h2 className="hh-auth-title">Start Finding Your Date</h2>
              <p className="hh-auth-lead">
                Create your profile and discover meaningful Jewish connections
                {selectedMarket?.name ? ` in ${selectedMarket.name}` : ''}.
              </p>
              <button
                type="button"
                className="login-signup-btn"
                onClick={() => navigate('/register')}
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Sign Up
              </button>
            </>
          ) : (
            <>
              <p className="hh-eyebrow" style={{ justifyContent: 'center' }}>New here?</p>
              <h2 className="hh-auth-title">Join Your City First</h2>
              <p className="hh-auth-lead">
                {launchLoading
                  ? 'Checking whether signup is open in your city…'
                  : selectedMarket?.name
                    ? `${selectedMarket.name} is still building its founding list. Join the waitlist, then create your profile when we open.`
                    : 'Signup opens when your city launches. Join the founding waitlist to be first through the door.'}
              </p>
              {!launchLoading && (
                <button
                  type="button"
                  className="login-signup-btn"
                  onClick={() => navigate('/#join')}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  Join Waitlist
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onSubmit={handleForgotPasswordSubmit}
      />
      
      {/* Toast Notifications */}
      <ToastContainer position="top-right" />
      </div>
    </>
  );
};

export default LoginForm;
